import { useEffect, type ReactNode } from 'react';
import {
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Edit from '@mui/icons-material/Edit';
import OpenInNew from '@mui/icons-material/OpenInNew';
import PersonAdd from '@mui/icons-material/PersonAdd';
import { RightColumnPaper } from '~/modules/matters/detail/MatterDetail.styles';
import { RAILS_EXPERTS_URL } from '~/shared/config';

type RightDetailTab = 'details' | 'retainers' | 'experts';

interface Props {
  rightDetailTab: RightDetailTab;
  setRightDetailTab: (value: RightDetailTab) => void;
  showExpertsTab: boolean;
  isAttorney: boolean;
  onEditMatterClick: () => void;
  onAddExpertsClick: () => void;
  retainersContent: ReactNode;
  expertsContent: ReactNode;
  detailsContent: ReactNode;
}

const tabTitle: Record<RightDetailTab, string> = {
  retainers: 'All Retainers',
  experts: 'Experts',
  details: 'Matter details',
};

const FALLBACK_TAB: RightDetailTab = 'details';

function isRightDetailTab(value: string | null): value is RightDetailTab {
  return value === 'details' || value === 'retainers' || value === 'experts';
}

export const MatterDetailRightColumn = ({
  rightDetailTab,
  setRightDetailTab,
  showExpertsTab,
  isAttorney,
  onEditMatterClick,
  onAddExpertsClick,
  retainersContent,
  expertsContent,
  detailsContent,
}: Props) => {
  // If we're on experts but the tab is hidden (e.g. matter lost experts), sync to fallback
  useEffect(() => {
    if (rightDetailTab === 'experts' && !showExpertsTab) {
      setRightDetailTab(FALLBACK_TAB);
    }
  }, [rightDetailTab, showExpertsTab, setRightDetailTab]);

  const effectiveTab =
    rightDetailTab === 'experts' && !showExpertsTab
      ? FALLBACK_TAB
      : rightDetailTab;

  return (
    <RightColumnPaper variant="outlined">
      <RightColumnHeader>
        <HeaderRow>
          <RightColumnTitle variant="h3">
            {tabTitle[effectiveTab]}
          </RightColumnTitle>
          {effectiveTab === 'details' && isAttorney && (
            <EditButton
              aria-label="Edit matter"
              onClick={onEditMatterClick}
              size="small"
            >
              <Edit sx={{ fontSize: 20 }} />
            </EditButton>
          )}
        </HeaderRow>
        <StyledToggleButtonGroup
          value={effectiveTab}
          exclusive
          onChange={(_, value) => {
            if (value !== null && isRightDetailTab(value)) {
              setRightDetailTab(value);
            }
          }}
          aria-label="Right panel view"
        >
          <StyledToggleButton value="retainers" aria-label="Retainers">
            Retainers
          </StyledToggleButton>
          {showExpertsTab && (
            <StyledToggleButton value="experts" aria-label="Experts">
              Experts
            </StyledToggleButton>
          )}
          <StyledToggleButton value="details" aria-label="Matter details">
            Details
          </StyledToggleButton>
        </StyledToggleButtonGroup>
        {effectiveTab === 'experts' && (
          <ExpertTabActionsRow>
            <HeaderActionButton
              variant="outlined"
              size="small"
              component="a"
              href={RAILS_EXPERTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNew sx={{ fontSize: 16 }} aria-hidden />}
              aria-label="Find experts (opens in new tab)"
            >
              Find
            </HeaderActionButton>
            <HeaderActionButton
              variant="outlined"
              size="small"
              startIcon={
                <PersonAdd sx={{ fontSize: 16, width: 16, height: 16 }} />
              }
              onClick={onAddExpertsClick}
            >
              Add
            </HeaderActionButton>
          </ExpertTabActionsRow>
        )}
      </RightColumnHeader>
      {effectiveTab === 'retainers' && retainersContent}
      {effectiveTab === 'experts' && expertsContent}
      {effectiveTab === 'details' && detailsContent}
    </RightColumnPaper>
  );
};

export const RightColumnHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const HeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
  },
}));

export const RightColumnTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.subtitle1.fontWeight,
  color: theme.palette.grey[800],
}));

const EditButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.amount.main,
  '&:hover': {
    color: theme.palette.amount.dark,
    backgroundColor: theme.palette.action.hover,
  },
}));

const ExpertTabActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  marginTop: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    '& .MuiButton-root': {
      flex: 1,
    },
  },
}));

const HeaderActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  [theme.breakpoints.down('sm')]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
})) as typeof Button;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  alignSelf: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    alignSelf: 'stretch',
    display: 'flex',
    '& .MuiToggleButton-root': {
      flex: 1,
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)({
  textTransform: 'none',
});
